import styled from '@emotion/styled/macro';
import {
  Button,
  Skeleton,
  SvgIcon,
  SvgNames,
  tokens,
} from '@sunrun/experience-ui-components';
import {
  getSplatUserType,
  UserProfile,
  FIELD_SALES,
  FUSION_CLOSER,
  INSIDE_SALES,
  DIRECT_TO_HOME,
  isSunrunManaged,
} from '@sunrun/sales-experience-shared';
import { publish } from 'helpers/messaging';
import { getRecheckUrl } from 'helpers/reCheckURL';
import {
  isFieldOrAIRHelper,
  isAffiliateHelper,
  isStreetOrFusionHelper,
} from 'helpers/repTypeHelper';

interface ResourcesSectionProps {
  userProfile: UserProfile;
}

const theRunThroughUrls = {
  [FUSION_CLOSER]:
    'https://current.sunrun.com/site/b7862463-82a0-531a-b264-209806cbe90f/dashboard',
  [DIRECT_TO_HOME]:
    'https://current.sunrun.com/site/ba74dc6e-b8c4-5742-9b86-5bb349771a9f/dashboard',
  [FIELD_SALES]:
    'https://current.sunrun.com/site/7e92d6fa-d1a7-5e1e-b807-4273b9fa5512/dashboard',
  [INSIDE_SALES]:
    'https://current.sunrun.com/site/f84222bb-aaed-5912-ab79-cb9dd8c4743c/dashboard',
};

const ResourcesSection = ({ userProfile }: ResourcesSectionProps) => {
  const splatUserType = getSplatUserType(userProfile);
  const isD2H = splatUserType === 'Direct to Home';
  const isFusion = userProfile.partnerName === 'Fusion';
  const showRunX = isSunrunManaged(userProfile);
  const isAffiliate = isAffiliateHelper(userProfile);
  const isFieldOrAIR = isFieldOrAIRHelper(userProfile);
  const isStreetOrFusion = isStreetOrFusionHelper(userProfile);

  const open = (url: string) => {
    window.open(url, '_blank');
  };

  return (
    <>
      <ResourcesTitle>Other resources</ResourcesTitle>
      <ResourcesGrid>
        <ResourceButton>
          <StyledResourceButton
            size="sm"
            color="secondary"
            onClick={() => publish.createLead()}>
            Create a Lead
            <StyledIcon
              name={SvgNames.Plus}
              height={20}
              width={20}
              color={tokens.BRAND_HEROBLUE}
            />
          </StyledResourceButton>
        </ResourceButton>

        <ResourceButton>
          {isFusion || isD2H ? (
            <StyledResourceButton
              size="sm"
              color="secondary"
              onClick={() =>
                open(
                  'https://10ay.online.tableau.com/#/site/sunrunbi/views/CommissionTracker/CommissionTracker',
                )
              }>
              View Pay
              <StyledIcon
                name={SvgNames.ArrowSquareOutSvg}
                height={20}
                width={20}
                color={tokens.BRAND_HEROBLUE}
              />
            </StyledResourceButton>
          ) : (
            <StyledResourceButton
              size="sm"
              color="secondary"
              onClick={() => open('https://sunrun.docebosaas.com/')}>
              Resource Center
              <StyledIcon
                name={SvgNames.ArrowSquareOutSvg}
                height={20}
                width={20}
                color={tokens.BRAND_HEROBLUE}
              />
            </StyledResourceButton>
          )}
        </ResourceButton>

        {(isFieldOrAIR || isStreetOrFusion || isAffiliate) && (
          <ResourceButton>
            <StyledResourceButton
              data-testid="recheck-button"
              size="sm"
              color="secondary"
              onClick={() => open(getRecheckUrl(userProfile))}>
              Recheck
              <StyledIcon
                name={SvgNames.ArrowSquareOutSvg}
                height={20}
                width={20}
                color={tokens.BRAND_HEROBLUE}
              />
            </StyledResourceButton>
          </ResourceButton>
        )}

        <ResourceButton>
          <StyledResourceButton
            size="sm"
            color="secondary"
            onClick={() => open('https://sunrun.okta.com/app/UserHome')}>
            My Apps
            <StyledIcon
              name={SvgNames.ArrowSquareOutSvg}
              height={20}
              width={20}
              color={tokens.BRAND_HEROBLUE}
            />
          </StyledResourceButton>
        </ResourceButton>

        <ResourceButton>
          <StyledResourceButton
            size="sm"
            color="secondary"
            onClick={() => open('http://current.sunrun.com/')}>
            Current
            <StyledIcon
              name={SvgNames.ArrowSquareOutSvg}
              height={20}
              width={20}
              color={tokens.BRAND_HEROBLUE}
            />
          </StyledResourceButton>
        </ResourceButton>

        <ResourceButton>
          <StyledResourceButton
            size="sm"
            color="secondary"
            onClick={() =>
              open(
                'https://current.sunrun.com/site/7b6848bd-8bfb-5e2e-b56d-41b94260487d/content?type=page&pageCategoryId=5daf3824-a623-53e2-a6e8-06a6ec0120a0',
              )
            }>
            Company News
            <StyledIcon
              name={SvgNames.ArrowSquareOutSvg}
              height={20}
              width={20}
              color={tokens.BRAND_HEROBLUE}
            />
          </StyledResourceButton>
        </ResourceButton>

        {/* Terros - shown for D2H or Fusion users */}

        {!userProfile ? (
          <ResourceButtonSkeleton />
        ) : (
          (isD2H || isFusion) && (
            <ResourceButton>
              <StyledResourceButton
                size="sm"
                color="secondary"
                onClick={() => publish.openTerros()}>
                Open Terros
                <StyledIcon
                  name={SvgNames.ArrowSquareOutSvg}
                  height={20}
                  width={20}
                  color={tokens.BRAND_HEROBLUE}
                />
              </StyledResourceButton>
            </ResourceButton>
          )
        )}

        <ResourceButton>
          <StyledResourceButton
            size="sm"
            color="secondary"
            onClick={() =>
              open(
                theRunThroughUrls[
                  splatUserType as keyof typeof theRunThroughUrls
                ],
              )
            }>
            The Run Through{' '}
            <StyledIcon
              name={SvgNames.ArrowSquareOutSvg}
              height={20}
              width={20}
              color={tokens.BRAND_HEROBLUE}
            />
          </StyledResourceButton>
        </ResourceButton>

        {!userProfile ? (
          <ResourceButtonSkeleton />
        ) : (
          showRunX && (
            <ResourceButton>
              <StyledResourceButton
                size="sm"
                color="secondary"
                onClick={() => open('https://shoprunx.sunrun.com/')}>
                Shop RunX
                <StyledIcon
                  name={SvgNames.ArrowSquareOutSvg}
                  height={20}
                  width={20}
                  color={tokens.BRAND_HEROBLUE}
                />
              </StyledResourceButton>
            </ResourceButton>
          )
        )}
      </ResourcesGrid>
    </>
  );
};

// Styled components
const ResourcesTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
`;

const ResourcesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  @media (max-width: 1024px) {
    flex-direction: column;
  }
`;

const ResourceButton = styled.div`
  min-width: fit-content;
  width: 192px;
`;

const StyledResourceButton = styled(Button)`
  min-width: fit-content;
  font-size: 16px;
  width: 192px;
`;

const StyledIcon = styled(SvgIcon)`
  margin: 0 8px -4px 8px;
`;

const ResourceButtonSkeleton = styled(Skeleton)`
  width: 100%;
`;

export { ResourcesSection };
